import { useAnalyticsInitialization } from "@faro-lotv/foreign-observers";
import { useLoggedInUser } from "@hooks/use-logged-in-user";
import { runtimeConfig } from "@src/runtime-config";
import { ReactChildrenOnly } from "@utils/utility-types";
import { AUTOMATION_TEST_USER_TAG } from "@utils/sentry-utils";
import { EmailUtils } from "@stellar/utils";
import { setTag, setUser } from "@sentry/react";
import { useEffect } from "react";
import { isProductionEnvironment } from "@utils/env-utils";

/**
 * Initializes and configures Amplitude Analytics for event tracking.
 *
 * This component should be placed where both user and company context are available
 * It handles:
 * - Amplitude initialization with proper user identification
 * - Default tracking properties setup (app name, environment, region)
 */
export function TrackEventInitializer({
  children,
}: ReactChildrenOnly): React.JSX.Element {
  const { currentUser } = useLoggedInUser();

  /**
   * Identify the logged-in user in Sentry so that error events can be attributed
   * and internal automation/test accounts can be filtered out (see `beforeSend`).
   *
   * Only the user id is sent to Sentry. The email is
   * used locally to derive the automation/test flag, which is stored as a tag so
   * `beforeSend` can drop those events without the email leaving the app.
   */
  useEffect(() => {
    setUser(currentUser?.id ? { id: currentUser.id } : null);
    setTag(
      AUTOMATION_TEST_USER_TAG,
      String(
        currentUser?.email
          ? EmailUtils.isAutomationTestUser(currentUser.email) &&
              !isProductionEnvironment
          : false
      )
    );
  }, [currentUser?.id, currentUser?.email]);

  useAnalyticsInitialization({
    app: "AdminPanel",
    keys: [runtimeConfig.features.amplitudeAPIKey],
    currentUserEmail: currentUser?.email,
    userId: currentUser?.id,
    region: runtimeConfig.appRegion,
    environment: runtimeConfig.environment,
    /* eslint-disable @typescript-eslint/naming-convention -- Library name */

    // Set it to false, since we already use the amplitude keys to distinguish internal users
    ignoreInternalUsers: false,

    // Set it to true, even though anonymous users cannot reach the AdminPanel
    ignoreAnonymousUsers: true,

    // Set it to false, to avoid any automatic tracking by Amplitude
    defaultTracking: false,
    /* eslint-enable @typescript-eslint/naming-convention */
  });

  // eslint-disable-next-line react/jsx-no-useless-fragment -- Needed to return React.JSX.Element
  return <>{children}</>;
}
